import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, SpecraBadge, SpecraButton, SpecraEntityCell, SpecraFilterToolbar, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { laborRatesHref, RecordSections, rateBookHref, SharedEditNotice } from '../../../components/SharedRecord';
import { useFeatureFlag } from '../../../contexts/featureFlagContext';
import { useTenantAreas } from '../../../lib/access/useTenantAreas';
import { companySizeLabel as registryCompanySizeLabel } from '../../Registries/companySize';
import { IdentifierSuggestionPanel } from '../../Registries/identifiers/IdentifierSuggestionPanel';
import { RegistryListNote } from '../../Registries/list/RegistryListNote';
import { OpenFullPage, useRowOpen } from '../../Registries/list/RowOpen';
import { useCompanySummaries } from '../../Registries/list/useRegistrySummaries';
import { useShowMore } from '../../Registries/list/useShowMore';
import { MergedRecordBanner } from '../../Registries/merge/MergedRecordBanner';
import { MergeRecordDialog } from '../../Registries/merge/MergeRecordDialog';
import { useGoToMergedSurvivor, useMergedRecordRedirect } from '../../Registries/merge/useMerge';
import { REGISTRIES_RECORDS_FLAG, useRegistryRecordsEnabled } from '../../Registries/registryFlags';
import { registryHref } from '../../Registries/registryHref';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { CompanyDocumentsSection } from './CompanyDocumentsSection';
import { CompanyEvidenceSection } from './CompanyEvidenceSection';
import { CompanyLineageSection } from './CompanyLineageSection';
import { CompanyVehiclesSection } from './CompanyVehiclesSection';
import { businessSizeOptions, companySizeLabel, samSizeNote } from './companySize';
import { companyProfileHooks, contactHooks } from './hooks';
import { OurCompanySection } from './OurCompanySection';
import { RecordUnavailable } from './RecordUnavailable';
import { useRegistryQuery, useRegistryRecord } from './registryParams';
// Radix Select.Item disallows an empty-string value (it reserves "" for
// the cleared/placeholder state), so we omit a "None" row and let the
// placeholder serve as the unselected state.
const socioeconomicOptions = [
    {
        value: 'SDB',
        label: 'Small Disadvantaged Business'
    },
    {
        value: 'WOSB',
        label: 'Woman-Owned Small Business'
    },
    {
        value: 'HUBZone',
        label: 'HUBZone'
    },
    {
        value: '8(a)',
        label: '8(a)'
    },
    {
        value: 'SDVOSB',
        label: 'Service-Disabled Veteran-Owned'
    },
    {
        value: 'VOSB',
        label: 'Veteran-Owned'
    }
];
const baseColumns = [
    {
        key: 'name',
        header: 'Name',
        render: (row)=>/*#__PURE__*/ _jsxs("span", {
                className: "flex items-center gap-2",
                children: [
                    row.name,
                    row.isOwnCompany && /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "info",
                        children: "Your organization"
                    })
                ]
            })
    },
    {
        key: 'cageCode',
        header: 'CAGE'
    },
    {
        key: 'uei',
        header: 'UEI'
    },
    {
        key: 'socioeconomicStatus',
        header: 'Socioeconomic'
    }
];
const fields = [
    {
        kind: 'text',
        name: 'name',
        label: 'Company name',
        required: true,
        maxLength: 200
    },
    {
        kind: 'text',
        name: 'cageCode',
        label: 'CAGE Code',
        maxLength: 20
    },
    {
        kind: 'text',
        name: 'dunsNumber',
        label: 'DUNS Number',
        maxLength: 20
    },
    {
        kind: 'text',
        name: 'uei',
        label: 'UEI',
        maxLength: 20
    },
    {
        kind: 'text',
        name: 'website',
        label: 'Website',
        maxLength: 500
    },
    {
        kind: 'select',
        name: 'socioeconomicStatus',
        label: 'Socioeconomic status',
        options: socioeconomicOptions
    },
    {
        kind: 'select',
        name: 'businessSize',
        label: 'Business size',
        options: businessSizeOptions,
        testId: Testids.deliveryRegistries.companyBusinessSize
    }
];
// Form defaults for a given record (null = create). Shared by the create modal
// and the drawer's inline edit so both surfaces start from the same shape.
const companyDefaults = (c)=>({
        id: c?.id ?? '',
        name: c?.name ?? '',
        cageCode: c?.cageCode ?? '',
        dunsNumber: c?.dunsNumber ?? '',
        uei: c?.uei ?? '',
        website: c?.website ?? '',
        socioeconomicStatus: c?.socioeconomicStatus ?? '',
        businessSize: c?.businessSize ?? ''
    });
// Read sections plus, per row, the inline-edit control. Every field the create
// form exposes carries an `editField` (the SAME config), so edit-in-place and
// create stay in lockstep.
function companyDetailSections(c, suggestUei) {
    return [
        {
            heading: 'Profile',
            rows: [
                {
                    label: 'Company name',
                    value: c.name || '—',
                    editField: {
                        kind: 'text',
                        name: 'name',
                        label: 'Company name',
                        required: true,
                        maxLength: 200
                    }
                },
                {
                    label: 'CAGE Code',
                    value: c.cageCode || '—',
                    editField: {
                        kind: 'text',
                        name: 'cageCode',
                        label: 'CAGE Code',
                        maxLength: 20
                    }
                },
                {
                    label: 'DUNS Number',
                    value: c.dunsNumber || '—',
                    editField: {
                        kind: 'text',
                        name: 'dunsNumber',
                        label: 'DUNS Number',
                        maxLength: 20
                    }
                },
                {
                    label: 'UEI',
                    // REG-61: with registries_records_v1 on, an empty UEI offers "Find UEI".
                    value: !c.uei && suggestUei ? /*#__PURE__*/ _jsx(IdentifierSuggestionPanel, {
                        canWrite: suggestUei.canWrite,
                        density: "inline",
                        kind: "COMPANY",
                        registryId: c.id,
                        registryName: c.name
                    }) : c.uei || '—',
                    editField: {
                        kind: 'text',
                        name: 'uei',
                        label: 'UEI',
                        maxLength: 20
                    }
                },
                {
                    label: 'Website',
                    value: c.website || '—',
                    editField: {
                        kind: 'text',
                        name: 'website',
                        label: 'Website',
                        maxLength: 500
                    }
                },
                {
                    label: 'Socioeconomic status',
                    value: c.socioeconomicStatus || '—',
                    editField: {
                        kind: 'select',
                        name: 'socioeconomicStatus',
                        label: 'Socioeconomic status',
                        options: socioeconomicOptions
                    }
                },
                {
                    label: 'Business size',
                    value: c.businessSize ? companySizeLabel(c.businessSize) : '—',
                    editField: {
                        kind: 'select',
                        name: 'businessSize',
                        label: 'Business size',
                        options: businessSizeOptions,
                        testId: Testids.deliveryRegistries.companyBusinessSize
                    }
                },
                // SAM's own value: read-only, written only by the SAM sync (never the form).
                ...c.samBusinessSize ? [
                    {
                        label: 'SAM size',
                        value: /*#__PURE__*/ _jsx(SpecraText, {
                            size: "meta",
                            tone: "muted",
                            "data-testid": Testids.deliveryRegistries.companySamBusinessSize,
                            children: samSizeNote(c.samBusinessSize, c.samBusinessSizeNaics, c.samBusinessSizeRetrievedAt)
                        })
                    }
                ] : []
            ]
        }
    ];
}
// ListCompanyProfiles returns the whole tenant registry with no limit/offset,
// and EntityTable has no virtualization — mounting all of it is a
// multi-second main-thread stall once a tenant has thousands of companies
// (the RPC itself stays fast at that volume; the render is what stalls,
// SAD-323). 200 matches the existing `limit: 200` convention in hooks.ts.
// This caps what gets MOUNTED, not what gets fetched — the fetch, and its
// shape, are unchanged.
const REGISTRY_ROW_CAP = 200;
const COMPANY_MERGE_FIELDS = [
    {
        id: 'uei',
        label: 'UEI',
        strong: true
    },
    {
        id: 'cageCode',
        label: 'CAGE Code'
    },
    {
        id: 'dunsNumber',
        label: 'DUNS Number'
    }
];
const companyMergeSide = (c)=>({
        id: c.id,
        name: c.name || 'Unnamed company',
        fields: {
            uei: c.uei,
            cageCode: c.cageCode,
            dunsNumber: c.dunsNumber
        }
    });
export function CompaniesTab() {
    const tenantId = useTenantId();
    const recordsOn = useRegistryRecordsEnabled();
    const [query, setQuery] = useRegistryQuery();
    const navigate = useNavigate();
    // SAD-948: the shared registries are edited only by area:registries holders.
    const canEdit = useTenantAreas().registries;
    const suggestOn = useFeatureFlag(REGISTRIES_RECORDS_FLAG);
    const { data, isLoading } = companyProfileHooks.useList({
        companyId: tenantId
    });
    const upsert = companyProfileHooks.useUpsert();
    const remove = companyProfileHooks.useDelete();
    const items = data?.items ?? [];
    // See REGISTRY_ROW_CAP above — only the rows handed to EntityTable are
    // capped; every other use of `items` (detail lookup, lineage picker) keeps
    // the full fetched list.
    const filteredItems = recordsOn && query ? items.filter((row)=>[
            row.name,
            row.cageCode,
            row.uei
        ].some((value)=>value?.toLowerCase().includes(query.toLowerCase()))) : items;
    const paging = useShowMore(filteredItems, query);
    const visibleItems = recordsOn ? paging.visible : items.slice(0, REGISTRY_ROW_CAP);
    const summaries = useCompanySummaries(recordsOn ? visibleItems.map((row)=>row.id) : []);
    const summaryById = new Map((summaries.isError ? [] : summaries.data ?? []).map((row)=>[
            row.companyId,
            row
        ]));
    // People (spec 10 / Phase 3 slice 1): "you can record which company a
    // person belongs to; you cannot ask a company who its people are." The
    // count column + the drawer's People section are the reverse read —
    // client-side join over the tenant contact list (already unpaged; the
    // affiliation write path is unchanged).
    const contactsQuery = contactHooks.useList({
        companyId: tenantId
    });
    const contactsByCompany = useMemo(()=>{
        const m = new Map();
        for (const c of contactsQuery.data?.items ?? []){
            if (!c.companyProfileId) continue;
            const list = m.get(c.companyProfileId) ?? [];
            list.push({
                id: c.id,
                name: `${c.firstName} ${c.lastName}`.trim() || c.email || 'Unnamed contact',
                title: c.title,
                email: c.email
            });
            m.set(c.companyProfileId, list);
        }
        return m;
    }, [
        contactsQuery.data
    ]);
    const columns = useMemo(()=>[
            ...baseColumns,
            {
                key: 'contacts',
                header: 'Contacts',
                // Row click opens the drawer whose People section IS the list.
                render: (row)=>/*#__PURE__*/ _jsx("span", {
                        "data-testid": Testids.deliveryRegistries.companyContactsCount,
                        children: contactsByCompany.get(row.id)?.length ?? 0
                    })
            }
        ], [
        contactsByCompany
    ]);
    const [formOpen, setFormOpen] = useState(false);
    // Store only the selected id and DERIVE the record from `items` — so an open
    // drawer always reflects the latest list (e.g. after an inline-edit Save)
    // without an effect that re-syncs state (which infinite-loops on fresh-but-
    // equal references and hangs the runner).
    // The id lives in the URL (?record=), so reload, a pasted link and browser
    // Back all reopen the same record. An id that resolves to nothing shows the
    // generic unavailable state rather than a blank page.
    const { recordId: detailId, openRecord, closeRecord } = useRegistryRecord();
    const detail = detailId ? items.find((c)=>c.id === detailId) ?? null : null;
    const missing = !!detailId && !isLoading && !detail;
    // A loser's URL lands on its survivor; only a record with no merge pointer is "unavailable".
    const redirect = useMergedRecordRedirect('COMPANY', detailId, missing);
    const recordUnavailable = missing && !redirect.resolving;
    const goToSurvivor = useGoToMergedSurvivor();
    const [merging, setMerging] = useState(false);
    const [searchParams] = useSearchParams();
    const returnTo = searchParams.get('returnTo');
    // Whether the detail drawer opens in read or straight into edit (row Edit).
    const [detailMode, setDetailMode] = useState('read');
    const rowOpen = useRowOpen('COMPANY', ()=>setDetailMode('read'));
    const [deleting, setDeleting] = useState(null);
    // Shared by the create modal and drawer edit-in-place; the target id is passed
    // in (empty = create).
    // The analyst size (and its MANUAL source) is sent only when the user changed
    // it; sam_* fields are never sent.
    const submitCompany = async (values, id, base)=>{
        const sizeChanged = (values.businessSize ?? '') !== (base?.businessSize ?? '');
        await upsert.mutateAsync({
            id,
            companyId: tenantId,
            name: values.name,
            cageCode: values.cageCode ?? '',
            dunsNumber: values.dunsNumber ?? '',
            uei: values.uei ?? '',
            website: values.website ?? '',
            socioeconomicStatus: values.socioeconomicStatus ?? '',
            ...sizeChanged && {
                businessSize: values.businessSize,
                businessSizeSource: 'MANUAL'
            }
        });
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    // The drawer's inline-edit config, bound to the viewed record.
    const detailEdit = detail ? {
        defaultValues: companyDefaults(detail),
        onSubmit: (v)=>submitCompany(v, detail.id, detail),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save changes'
    } : undefined;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            recordsOn && /*#__PURE__*/ _jsx(SpecraFilterToolbar, {
                search: {
                    value: query,
                    onChange: (e)=>setQuery(e.target.value),
                    placeholder: 'Search by name, CAGE or UEI',
                    ...{
                        'data-testid': Testids.deliveryRegistries.companiesSearch
                    }
                }
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: canEdit && /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: ()=>setFormOpen(true),
                    "data-testid": Testids.deliveryRegistries.companyCreate,
                    children: "New Company"
                })
            }),
            recordUnavailable && /*#__PURE__*/ _jsx(RecordUnavailable, {
                testId: Testids.deliveryRegistries.recordUnavailable,
                onDismiss: closeRecord
            }),
            !recordsOn && !isLoading && items.length > REGISTRY_ROW_CAP && /*#__PURE__*/ _jsxs("p", {
                className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                children: [
                    "Showing the first ",
                    REGISTRY_ROW_CAP.toLocaleString(),
                    " of ",
                    items.length.toLocaleString(),
                    ' ',
                    items.length === 1 ? 'company' : 'companies',
                    "."
                ]
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: visibleItems,
                columns: recordsOn ? [
                    {
                        key: 'name',
                        header: 'Company',
                        render: (row)=>/*#__PURE__*/ _jsx(SpecraEntityCell, {
                                title: row.name,
                                description: [
                                    registryCompanySizeLabel(row.businessSize, row.samBusinessSize),
                                    row.socioeconomicStatus,
                                    row.cageCode && `CAGE ${row.cageCode}`
                                ].filter(Boolean).join(' · '),
                                trailing: row.isOwnCompany ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: "dark",
                                    children: "Your organization"
                                }) : undefined
                            })
                    },
                    {
                        key: 'relationship',
                        header: 'To us',
                        render: (row)=>{
                            const summary = summaryById.get(row.id);
                            const priority = [
                                'OURS',
                                'COMPETITOR',
                                'PRIME_WE_SUB_TO',
                                'TEAMMATE',
                                'OUR_SUB',
                                'PART_OF'
                            ];
                            const primary = summary?.relationships.filter((r)=>r.kind !== 'COMPETITOR' || !r.until).sort((a, b)=>priority.indexOf(a.kind) - priority.indexOf(b.kind))[0];
                            const labels = {
                                OURS: 'Ours',
                                COMPETITOR: 'Competitor',
                                PRIME_WE_SUB_TO: 'Prime we sub to',
                                TEAMMATE: 'Teammate',
                                OUR_SUB: 'Our sub',
                                PART_OF: 'Part of'
                            };
                            return primary ? /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: primary.kind === 'OURS' ? 'dark' : primary.kind === 'COMPETITOR' ? 'danger' : primary.kind === 'PRIME_WE_SUB_TO' ? 'info' : [
                                            'TEAMMATE',
                                            'OUR_SUB'
                                        ].includes(primary.kind) ? 'success' : 'neutral',
                                        children: labels[primary.kind] ?? primary.kind
                                    }),
                                    primary.qualifier && /*#__PURE__*/ _jsx(SpecraText, {
                                        size: "meta",
                                        tone: "muted",
                                        children: primary.qualifier
                                    })
                                ]
                            }) : '—';
                        }
                    },
                    {
                        key: 'vehicles',
                        header: 'Vehicles',
                        render: (row)=>{
                            const s = summaryById.get(row.id);
                            return s?.vehicleHoldings.length ? s.vehicleHoldings.map((h)=>h.vehicle?.name).filter(Boolean).join(', ') : s?.priceListVehicles.length ? /*#__PURE__*/ _jsxs(SpecraText, {
                                size: "meta",
                                tone: "muted",
                                children: [
                                    "price list on ",
                                    s.priceListVehicles.map((v)=>v.name).join(', ')
                                ]
                            }) : '—';
                        }
                    },
                    {
                        key: 'rates',
                        header: 'Rate book',
                        render: (row)=>{
                            const s = summaryById.get(row.id);
                            return s && (s.ceilingCount || s.realRateCount) ? /*#__PURE__*/ _jsx(SpecraEntityCell, {
                                title: `${s.ceilingCount} ceilings`,
                                description: s.realRateCount ? `${s.realRateCount} real rates` : 'no real rates'
                            }) : '—';
                        }
                    },
                    {
                        key: 'people',
                        header: 'People we know',
                        align: 'right',
                        render: (row)=>summaryById.get(row.id)?.knownPeopleCurrent ?? '—'
                    },
                    {
                        key: 'appearances',
                        header: 'Shows up on',
                        render: (row)=>{
                            const s = summaryById.get(row.id);
                            return s && (s.visiblePursuitCount || s.visibleContractCount) ? `${s.visiblePursuitCount} pursuits · ${s.visibleContractCount} contracts` : '—';
                        }
                    }
                ] : columns,
                testid: Testids.deliveryRegistries.companiesTable,
                rowTestid: Testids.deliveryRegistries.companyRow,
                rowKey: (c)=>c.id,
                isLoading: isLoading,
                onRowClick: (row)=>{
                    rowOpen.onRowClick(row.id);
                },
                hideRowActions: !canEdit && !recordsOn,
                rowActions: (row)=>// One quiet row menu instead of three buttons per row (mockup: no visible row actions).
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex justify-end",
                        children: /*#__PURE__*/ _jsxs(DropdownMenu, {
                            children: [
                                /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                                    asChild: true,
                                    children: /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "xs",
                                        variant: "ghost",
                                        "aria-label": `Actions for ${row.name}`,
                                        "data-testid": Testids.deliveryRegistries.companyRowActions,
                                        onClick: (e)=>e.stopPropagation(),
                                        children: "⋯"
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(DropdownMenuContent, {
                                    align: "end",
                                    onClick: (e)=>e.stopPropagation(),
                                    children: [
                                        rowOpen.quickLookEnabled && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                            "data-testid": Testids.deliveryRegistries.quickLook,
                                            onSelect: ()=>rowOpen.quickLook(row.id),
                                            children: "Quick look"
                                        }),
                                        canEdit && /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                                    // Edit-in-place: open the drawer straight into edit mode.
                                                    onSelect: ()=>{
                                                        setDetailMode('edit');
                                                        openRecord(row.id);
                                                    },
                                                    "data-testid": Testids.deliveryRegistries.companyEdit,
                                                    children: "Edit"
                                                }),
                                                /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                                    className: "text-danger focus:text-danger",
                                                    onSelect: ()=>setDeleting(row),
                                                    "data-testid": Testids.deliveryRegistries.companyDelete,
                                                    children: "Delete…"
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        })
                    })
            }),
            recordsOn && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    paging.hasMore && /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": Testids.deliveryRegistries.showMore,
                        onClick: paging.showMore,
                        children: "Show more"
                    }),
                    /*#__PURE__*/ _jsx(RegistryListNote, {
                        children: "Teammates and competitors are often the same company at different times. The row keeps both."
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "Create Company",
                fields: fields,
                defaultValues: companyDefaults(null),
                onSubmit: async (v)=>{
                    await submitCompany(v, '', null);
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending
            }),
            /*#__PURE__*/ _jsxs(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && closeRecord(),
                title: detail ? detail.name || 'Company' : '',
                sections: detail ? companyDetailSections(detail, suggestOn ? {
                    canWrite: canEdit
                } : undefined) : [],
                testid: Testids.deliveryRegistries.companyDetailDrawer,
                edit: detailEdit,
                canEdit: canEdit,
                initialMode: detailMode,
                children: [
                    detail && /*#__PURE__*/ _jsx(OpenFullPage, {
                        kind: "COMPANY",
                        id: detail.id
                    }),
                    detail ? /*#__PURE__*/ _jsx(RecordSections, {
                        label: "Company record sections",
                        sections: [
                            {
                                key: 'overview',
                                label: 'Overview',
                                content: /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-4",
                                    children: [
                                        /*#__PURE__*/ _jsx(MergedRecordBanner, {
                                            kind: "COMPANY",
                                            survivorId: detail.id
                                        }),
                                        canEdit && !detail.isOwnCompany && /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            variant: "outline",
                                            onClick: ()=>setMerging(true),
                                            "data-testid": Testids.registryMerge.open,
                                            children: "Merge into…"
                                        }),
                                        /*#__PURE__*/ _jsx(SharedEditNotice, {
                                            record: "Company",
                                            scope: "every pursuit, program and proposal"
                                        }),
                                        /*#__PURE__*/ _jsx(OurCompanySection, {
                                            companyProfileId: detail.id,
                                            isOwnCompany: !!detail.isOwnCompany,
                                            companies: items.map((c)=>({
                                                    id: c.id,
                                                    name: c.name
                                                }))
                                        }),
                                        /*#__PURE__*/ _jsx(CompanyLineageSection, {
                                            companies: items.map((c)=>({
                                                    id: c.id,
                                                    name: c.name
                                                })),
                                            companyProfileId: detail.id
                                        }),
                                        /*#__PURE__*/ _jsx(Link, {
                                            to: `/library?provider=${encodeURIComponent(detail.id)}`,
                                            className: "inline-block text-xs text-primary underline-offset-2 hover:underline",
                                            "data-testid": Testids.sharedRecord.librarySummariesLink,
                                            children: "Library summaries for this company"
                                        })
                                    ]
                                })
                            },
                            {
                                key: 'people',
                                label: 'People',
                                content: /*#__PURE__*/ _jsx(_Fragment, {
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        "data-testid": Testids.deliveryRegistries.companyPeopleSection,
                                        className: "space-y-2",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                                                children: "People"
                                            }),
                                            /*#__PURE__*/ _jsx("p", {
                                                className: "text-xs text-muted-foreground",
                                                children: "Affiliation does not imply staffing, an LCAT, a platform account or access to this company's connected work (C1.1-05)."
                                            }),
                                            (detail ? contactsByCompany.get(detail.id) ?? [] : []).map((p)=>/*#__PURE__*/ _jsxs(SpecraButton, {
                                                    variant: "ghost",
                                                    fullWidth: true,
                                                    "data-testid": Testids.deliveryRegistries.companyPeopleRowLink,
                                                    className: "h-auto flex-col items-start justify-start rounded-md border border-border/60 p-2 text-left text-[length:var(--fs-ui-caption)]",
                                                    onClick: ()=>navigate(registryHref({
                                                            kind: 'CONTACT',
                                                            id: p.id
                                                        }, returnTo ?? undefined) ?? '/registries?tab=contacts'),
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "font-medium",
                                                            children: p.name
                                                        }),
                                                        (p.title || p.email) && /*#__PURE__*/ _jsx("span", {
                                                            className: "text-muted-foreground",
                                                            children: [
                                                                p.title,
                                                                p.email
                                                            ].filter(Boolean).join(' · ')
                                                        })
                                                    ]
                                                }, p.id)),
                                            detail && (contactsByCompany.get(detail.id) ?? []).length === 0 && /*#__PURE__*/ _jsx("p", {
                                                className: "text-xs text-muted-foreground",
                                                children: "No contacts affiliated with this company yet — set a contact's company in Registries → Contacts."
                                            })
                                        ]
                                    })
                                })
                            },
                            {
                                key: 'documents',
                                label: 'Documents',
                                content: /*#__PURE__*/ _jsx(CompanyDocumentsSection, {
                                    profileId: detail.id
                                })
                            },
                            {
                                key: 'vehicles',
                                label: 'Vehicles',
                                content: /*#__PURE__*/ _jsx(CompanyVehiclesSection, {
                                    tenantId: tenantId,
                                    profileId: detail.id
                                })
                            },
                            {
                                key: 'labor',
                                label: 'Labor & Rates',
                                content: /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-3",
                                    children: [
                                        /*#__PURE__*/ _jsx(CompanyEvidenceSection, {
                                            tenantId: tenantId,
                                            profileId: detail.id
                                        }),
                                        /*#__PURE__*/ _jsx(Link, {
                                            to: laborRatesHref(''),
                                            className: "inline-block text-xs text-primary underline-offset-2 hover:underline",
                                            "data-testid": Testids.sharedRecord.laborRatesLink,
                                            children: "Open Labor & Rates"
                                        }),
                                        /*#__PURE__*/ _jsx(Link, {
                                            to: rateBookHref({
                                                company: detail.id
                                            }),
                                            className: "inline-block text-xs text-primary underline-offset-2 hover:underline",
                                            "data-testid": Testids.deliveryRegistries.rateBookOpen,
                                            children: "Open in Rate Book"
                                        })
                                    ]
                                })
                            },
                            // Capabilities describe what OUR organization can do; another
                            // Company has no such section.
                            ...detail.isOwnCompany ? [
                                {
                                    key: 'capabilities',
                                    label: 'Capabilities',
                                    content: /*#__PURE__*/ _jsx(Link, {
                                        to: "/registries?tab=capabilities",
                                        className: "inline-block text-xs text-primary underline-offset-2 hover:underline",
                                        children: "Open our Capabilities"
                                    })
                                }
                            ] : []
                        ]
                    }) : null
                ]
            }),
            merging && detail && /*#__PURE__*/ _jsx(MergeRecordDialog, {
                kind: "COMPANY",
                open: true,
                onOpenChange: setMerging,
                loser: companyMergeSide(detail),
                candidates: items.filter((c)=>c.id !== detail.id).map(companyMergeSide),
                fieldDefs: COMPANY_MERGE_FIELDS,
                onMerged: goToSurvivor
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Company",
                description: deleting ? `Remove "${deleting.name}" from the companies directory? It will be deactivated and restorable, not erased — existing references (contacts, rates, lineage, vehicle records, and historical program/pursuit references) are preserved and continue to identify this organization, and an administrator can restore it.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            })
        ]
    });
}
